import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { unitId, actionId } from '@/annotations'
import { ModalHeader, ModalBody, ModalFooter } from '@/components/Modal'
import { Modal } from '@/screens/shared/ModalScrim'
import { Button } from '@/components/Button'
import { Field } from '@/components/Field'
import { Note } from '@/components/Note'
import { Strip } from '@/components/Strip'
import { getApiClient, unwrap, ApiProblemError } from '@/data/apiClient'
import { UnexpectedRefusal, isUnexpectedRefusal } from '@/screens/auth-shared/Refusals'
import { M_STEPUP_COPY } from '@/copy/modals'
import type { components } from '@/api/generated/schema'
import type { Problem } from '@/data/apiClient'

type RestoreInput = components['schemas']['RestoreInput']
type RestoreConfirmation = components['schemas']['RestoreConfirmation']

/**
 * `m-stepup` — process/09-SCREEN-SPECS/m-stepup.md. Mints the confirmation assertion
 * (`restoreCreateConfirmation`) — it runs no Cognito challenge (`D70`, W55): what the prototype
 * once drew as a six-digit field is dead, and this modal collects the write-order acknowledgement
 * (rendered here, not re-fetched — the caller already built `restoreInput` from the plan the
 * operator saw) and a **typed** destination confirmation instead.
 *
 * `restoreInput` is the wizard's own (the restore wizard, Pass 6, not built yet) — this modal is
 * the last step of that flow and is built standalone here, the same "caller doesn't exist yet"
 * pattern `OnboardingLayer`/`ShellLayer` were built under before W143 gave them one.
 *
 * **The captcha is UI-only friction, not a request field.** Neither `RestoreInput` nor
 * `RestoreConfirmation` has a place for it — the spec's own rule 4 calls it "a declared speed bump
 * rather than as the control," which is exactly what a client-side-only gate is. Reported here
 * rather than invented as a phantom API field.
 */
export function MStepup({
  orgId,
  workspaceId,
  restoreInput,
  scopeSummary,
  kindLabel,
  destinationLabel,
  onDismiss,
  onConfirmed,
}: {
  orgId: string
  workspaceId: string
  restoreInput: RestoreInput
  scopeSummary: string
  kindLabel: string
  destinationLabel: string
  onDismiss: () => void
  onConfirmed: (confirmation: RestoreConfirmation) => void
}) {
  const [typedDestination, setTypedDestination] = useState('')
  const [speedBumpChecked, setSpeedBumpChecked] = useState(false)

  const confirm = useMutation({
    mutationFn: async () => {
      const client = getApiClient()
      const response = await client.POST('/orgs/{org_id}/workspaces/{workspace_id}/restore-confirmations', {
        params: { path: { org_id: orgId, workspace_id: workspaceId } },
        body: restoreInput,
      })
      return unwrap(response as { data?: RestoreConfirmation; error?: Problem })
    },
    onSuccess: onConfirmed,
  })

  const problem = confirm.error instanceof ApiProblemError ? confirm.error.problem : undefined
  const destinationMatches = typedDestination === destinationLabel
  const headingId = 'm-stepup-heading'

  return (
    <Modal dataScr={unitId('m-stepup')} labelledBy={headingId} onDismiss={onDismiss} maxWidth={420}>
      <ModalHeader id={headingId} heading={M_STEPUP_COPY.heading}>
        {M_STEPUP_COPY.subheading}
      </ModalHeader>
      <ModalBody>
        {problem && (isUnexpectedRefusal(problem.refusal_kind) ? <UnexpectedRefusal problem={problem} /> : <p className="text-fail text-body mb-6">{problem.title}</p>)}

        <Strip>{M_STEPUP_COPY.scopeNoteTemplate(scopeSummary, destinationLabel)}</Strip>
        <Note>{M_STEPUP_COPY.kindNoteTemplate(kindLabel)}</Note>

        <Field label={M_STEPUP_COPY.destinationLabelTemplate(destinationLabel)}>
          <input
            type="text"
            value={typedDestination}
            onChange={(e) => setTypedDestination(e.target.value)}
            className="w-full border border-bd2 rounded-1 py-3 px-4 text-body-lg font-mono"
          />
        </Field>

        <label className="flex items-center gap-4 mt-6 text-body-lg bg-row border border-bd rounded-2 py-4 px-5">
          <input type="checkbox" checked={speedBumpChecked} onChange={(e) => setSpeedBumpChecked(e.target.checked)} />
          <span>{M_STEPUP_COPY.speedBumpLabel}</span>
        </label>

        <Note>{M_STEPUP_COPY.survivingNote}</Note>
      </ModalBody>
      <ModalFooter>
        <Button data-act={undefined} ghost onClick={onDismiss}>
          {M_STEPUP_COPY.cancelButton}
        </Button>
        <Button data-act={actionId('restore.confirm')} disabled={!destinationMatches || !speedBumpChecked || confirm.isPending} onClick={() => confirm.mutate()}>
          {M_STEPUP_COPY.confirmButton}
        </Button>
      </ModalFooter>
    </Modal>
  )
}
